import React, { useState, useContext } from "react";
import { TableBody, TableCell, TableRow } from "@windmill/react-ui";
import SalesDrawer from "components/drawer/SalesDrawer";
import MainDrawer from "components/drawer/MainDrawer";
import SalesViewModal from "components/modal/ViewSalesModel";
import useToggleDrawer from "hooks/useToggleDrawer";
import { notifySuccess, notifyError } from "utils/toast";
import { FiX } from "react-icons/fi";
import { FiEye } from "react-icons/fi";
import Tooltip from "../tooltip/Tooltip";
import ReceivedAmtModal from "components/modal/salesAmountModel";
import SalesServices from "services/SalesService";
import { Modal, ModalBody } from "@windmill/react-ui";
import { SidebarContext } from "context/SidebarContext";

const SalesTable = ({
  Sales: sales = [],
  perpage = 10,
  currentPage = 1,
  tab = "",
  hideEdit = false,
  fetchSales = () => {},
}) => {
  const { title, serviceId, handleModalOpen, handleView, handleUpdate } =
    useToggleDrawer();

  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveRowData, setReceiveRowData] = useState(null);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [selectedSalesData, setSelectedSalesData] = useState(null);
  const [openRemarkModal, setOpenRemarkModal] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState([]);
  const [cannotEdit, setCannotEdit] = useState(false);
  const [remarkModalVisible, setRemarkModalVisible] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [openEditPriceModal, setOpenEditPriceModal] = useState(false);
  const [confirmUpdateModal, setConfirmUpdateModal] = useState(false);

  const [newSellingPrice, setNewSellingPrice] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedUpdateId, setSelectedUpdateId] = useState(null);
  const { setIsUpdate } = useContext(SidebarContext);

  function parseDMY(dateStr) {
    const [day, month, year] = dateStr.split("-");
    return new Date(`${year}-${month}-${day}`); // YYYY-MM-DD
  }
  const handleShowRemark = (remark) => {
    setRemarkText(remark || "No remark provided.");
    setRemarkModalVisible(true);
  };

  const handleCloseModal = () => {
    setRemarkModalVisible(false);
  };

  const handleViewClick = async (id) => {
    try {
      const res = await SalesServices.getSalesById(id);
      // console.log("view form data--", res);

      if (res.status && res.data) {
        setSelectedSalesData(res.data);
        setShowSalesModal(true);
      } else {
        notifyError("No sales data found");
      }
    } catch (err) {
      notifyError("Failed to fetch sales data");
    }
  };

  const handleEditClick = async (id, index, computedData, canEdit) => {
    try {
      // console.log(canEdit)
      setCannotEdit(canEdit);
      const res = await SalesServices.getSalesByClientAgent(id);
      if (res.status && res.data) {
        // Calculate total received amount
        const totalReceived = Array.isArray(computedData.sales_records)
          ? computedData.sales_records.reduce(
              (sum, rec) => sum + parseFloat(rec.received_amt || 0),
              0,
            )
          : 0;
        // Calculate payable amount
        const payableAmt = (
          parseFloat(computedData.receivable_amt || 0) - totalReceived
        ).toFixed(2);

        const updatedRowData = {
          ...res.data,
          gst_amount: res.data.gst_amount ?? 0,
          tds_amount: res.data.tds_amount ?? 0,
          receivable_amt: computedData.receivable_amt ?? 0,
          payable_amt: payableAmt, // Add payable amount
        };
        setReceiveRowData(updatedRowData);
        setModalMode("edit");
        setShowReceiveModal(true);
      } else {
        notifyError("No sales data found");
      }
    } catch (err) {
      console.error(err);
      notifyError("Failed to fetch sales data");
    }
  };
  // GST/TDS/receivable/balance are computed once, server-side (salesModel.js
  // computeGstTds), and come back on each row as gst_amount/tds_amount/
  // receivable_amt/balance_amt — this table only renders them, it never
  // recomputes tax math itself.

  const openReceiveModal = (rowData, index, computedData) => {
    // Calculate total received amount
    const totalReceived = Array.isArray(computedData.sales_records)
      ? computedData.sales_records.reduce(
          (sum, rec) => sum + parseFloat(rec.received_amt || 0),
          0,
        )
      : 0;
    // Calculate payable amount
    const payableAmt = (
      parseFloat(computedData.receivable_amt || 0) - totalReceived
    ).toFixed(2);

    const updatedRowData = {
      ...rowData,
      gst_amount: computedData.gst_amount ?? 0,
      tds_amount: computedData.tds_amount ?? 0,
      receivable_amt: computedData.receivable_amt ?? 0,
      payable_amt: payableAmt, // Add payable amount
    };
    setReceiveRowData(updatedRowData);
    setModalMode("add");
    setShowReceiveModal(true);
  };

  // Rows already carry server-computed gst_amount/tds_amount/receivable_amt/
  // balance_amt (see getAllSales in salesModel.js) — no client-side math.
  const computedSales = sales;

  const handleModalSuccess = (result) => {
    if (result.success && typeof fetchSales === "function") {
      fetchSales();
    }
  };
  // console.log("Full computedSales data going into table:", computedSales);

  const adminInfo = JSON.parse(localStorage.getItem("adminInfo"));
  const profileId = adminInfo?.id_profile;

  const updateSellingPriceAPI = async (id) => {
    try {
      const payload = {
        selling_price: newSellingPrice,
        // or selectedRow?.ref_no depending on backend
      };

      const res = await SalesServices.updateSellingPrice(id, payload);

      if (res.status) {
        notifySuccess("Updated successfully");
        setConfirmUpdateModal(false);
        setOpenEditPriceModal(false);
        setIsUpdate(true);
      } else {
        notifyError("Failed to update");
      }
    } catch (err) {
      console.error(err);
      notifyError("Error updating selling price");
    }
  };

  const handleOpenEditPrice = async (id) => {
    try {
      const res = await SalesServices.getSalesById(id);

      if (res.status && res.data) {
        setNewSellingPrice(res.data.selling_price); // <-- set old price
        setOpenEditPriceModal(true); // <-- now open modal
        setSelectedUpdateId(id);
      } else {
        notifyError("Failed to load selling price");
      }
    } catch (err) {
      console.error(err);
      notifyError("Error fetching sales data");
    }
  };

  return (
    <>
      <MainDrawer>
        <SalesDrawer id={serviceId} tab={tab} />
      </MainDrawer>

      <TableBody>
        {/* {console.log(computedSales)} */}
        {computedSales.slice(0, perpage).map((data, index) => {
          const startDate = parseDMY(data.start_date);
          const endDate = parseDMY(data.end_date);

          // console.log("start date parsed", startDate);
          // console.log("end date parsed", endDate);

          const today = new Date();

          // Add 2 days to end date
          const endDatePlusTwo = new Date(endDate);
          endDatePlusTwo.setDate(endDatePlusTwo.getDate() + 2);
          // console.log("end date for edit", endDatePlusTwo);

          // Check if today is within allowed window
          const isWithinEditWindow =
            today >= startDate && today <= endDatePlusTwo;

          // Only profile 32 / 33 + trip not closed
          const canEditSellingPrice =
            (profileId === 32 || profileId === 33) &&
            isWithinEditWindow &&
            data.trip_status !== 1;

          // console.log("can edit?", canEditSellingPrice);

          return (
            <TableRow
              key={data.id ?? index}
              className={data.trip_status === 1 ? "bg-red-100" : ""}
            >
              <TableCell className="text-xs text-center">
                {(currentPage - 1) * perpage + index + 1}
              </TableCell>
              <TableCell className="text-center">
                <div className="text-center">
                  <span className="text-sm">{data.client_ref || "-"}</span>
                </div>
                <div className="text-center pt-2 text-blue-400">
                  <span className="text-sm">{data.agentref || "-"}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-center">
                  <span className="text-sm">{data.agentname || "-"}</span>
                </div>
                <div className="text-center">
                  <span
                    className={`text-sm ${
                      data.online_or_offline === "Online"
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {data.online_or_offline || "-"}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-center text-sm">
                {data.client_name || "-"}
              </TableCell>
              <TableCell className="text-center text-sm">
                <div>
                  <span className="text-green-500 font-medium">A:</span>{" "}
                  <span>{data.arrival || "-"}</span>
                </div>
                <div className="pt-3">
                  <span className="text-red-500 font-medium">D:</span>{" "}
                  <span>{data.departure || "-"}</span>
                </div>
              </TableCell>
              <TableCell className="text-center">
                {data.start_date && data.end_date ? (
                  <>
                    <div>
                      {new Date(
                        data.start_date.split("-").reverse().join("-"),
                      ).toLocaleDateString("en-GB")}
                    </div>
                    <div className="pt-3">
                      {new Date(
                        data.end_date.split("-").reverse().join("-"),
                      ).toLocaleDateString("en-GB")}
                    </div>
                  </>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="text-center">
                {data.trip_status === 1 && data.normal_selling ? (
                  <>
                    <div className="text-black text-sm font-medium">
                      {Number(data.normal_selling)}
                    </div>
                    <div
                      className="text-red-600 font-medium text-xs mt-1"
                      title="Cancellation charge — now used as the selling price for GST/TDS/Receivable below"
                    >
                      {Number(data.selling_price || 0)}
                    </div>
                  </>
                ) : (
                  <div className="text-black text-sm font-medium">
                    {Number(data.selling_price || 0)}
                  </div>
                )}
                {canEditSellingPrice && (
                  <button
                    className="text-blue-600 text-xs underline ml-2"
                    onClick={() => handleOpenEditPrice(data.client_id)}
                  >
                    Edit
                  </button>
                )}
              </TableCell>

              <TableCell className="text-center">
                {Number(data.basic_fare ?? 0).toFixed(2)}
              </TableCell>

              <TableCell className="text-center">
                <div>{Number(data.gst_amount ?? 0).toFixed(2)}</div>
              </TableCell>
              <TableCell className="text-center">
                {data.addon_rate ?? 0}
              </TableCell>

              <TableCell className="text-center">
                {data.trip_status === 1 ? (
                  data.cancellation_refundable ? (
                    <div
                      className="text-red-600 font-medium text-sm"
                      title={`Trip Cancelled In Between – Refundable: ₹${Number(
                        data.cancellation_refundable,
                      )}`}
                    >
                      {Number(data.selling_price || 0)}
                    </div>
                  ) : Array.isArray(data.sales_records) &&
                    data.sales_records.length > 0 ? (
                    <>
                      {/* CASE 2: Cancelled normally with sales */}
                      <div className="text-red-600 font-medium text-sm">
                        {Number(data.selling_price || 0)}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* CASE 3: Normal selling price fallback */}
                      <div className="text-red-600 text-sm font-medium">
                        {Number(data.selling_price || 0)}
                      </div>
                    </>
                  )
                ) : (
                  <div className="text-gray-400 text-sm font-medium">-</div>
                )}
              </TableCell>

              <TableCell className="text-center">
                <div>{Number(data.tds_amount ?? 0).toFixed(2)}</div>
              </TableCell>

              <TableCell className="text-center">
                {Number(data.receivable_amt ?? 0)}
              </TableCell>
              <TableCell className="text-center">
                {Array.isArray(data.sales_records) &&
                data.sales_records.length > 0
                  ? data.sales_records.map((rec, idx) => (
                      <div key={idx} className="mb-1">
                        {rec.received_amt || "0.00"}
                      </div>
                    ))
                  : "–"}
              </TableCell>
              <TableCell className="text-center">
                {Array.isArray(data.sales_records) &&
                data.sales_records.length > 0
                  ? data.sales_records.map((rec, idx) => (
                      <div key={idx} className="mb-1">
                        {rec.received_date || "-"}
                      </div>
                    ))
                  : "-"}
              </TableCell>
              {/* <TableCell className="text-center">
                {Array.isArray(data.sales_records) &&
                data.sales_records.length > 0
                  ? data.sales_records.map((rec, idx) => (
                      <div key={idx} className="mb-1">
                        <div>{rec.payment_type || "-"}</div>
                        {Array.isArray(rec.adjusted_from) &&
                          rec.adjusted_from.length > 0 && (
                            <div
                              className="text-xs text-blue-600"
                              title="This payment was made up partly or fully from another booking's leftover credit"
                            >
                              {rec.adjusted_from.map((af, aidx) => (
                                <div key={aidx}>
                                  from {af.from_ref_no || af.from_client_id}: ₹
                                  {Number(af.used_amount).toFixed(2)}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    ))
                  : "-"}
              </TableCell> */}

              <TableCell className="text-center">
                {Array.isArray(data.sales_records) &&
                data.sales_records.length > 0
                  ? data.sales_records.map((rec, idx) => (
                      <div key={idx} className="mb-2">
                        {rec.payment_type === "Adjust" ? (
                          <div>
                            <div className="text-sm ">
                              {rec.payment_type}
                            </div>

                            {/* {Array.isArray(rec.adjusted_from) &&
                            rec.adjusted_from.length > 0 ? (
                              rec.adjusted_from.map((adjusted, adjustedIdx) => (
                                <div
                                  key={adjustedIdx}
                                  className="text-xs"
                                >
                                  {adjusted.from_ref_no || "-"}
                                </div>
                              ))
                            ) : (
                              <div className="text-xs text-gray-400">-</div>
                            )} */}
                          </div>
                        ) : (
                          <div className="text-sm">{rec.payment_type || "-"}</div>
                        )}
                      </div>
                    ))
                  : "-"}
              </TableCell>

              {/* <TableCell className="text-center">
                {Array.isArray(data.sales_records) &&
                data.sales_records.length > 0
                  ? (() => {
                      const latestRefundable = data.sales_records
                        .slice()
                        .reverse()
                        .find(
                          (rec) =>
                            rec.refundable_amount !== null &&
                            rec.refundable_amount !== undefined,
                        );

                      const canEdit = !!latestRefundable; // true if found

                      // Online-payment rows synthesized from razorpay_transaction
                      // (no real `sales` row yet) carry rec.id === null — there's
                      // nothing in `sales` to edit, so no Edit action for those.
                      return data.sales_records.map((rec, idx) => (
                        <div
                          key={idx}
                          className="mb-1 flex justify-center gap-2"
                        >
                          {rec.id != null ? (
                            <span
                              className="text-blue-600 cursor-pointer font-medium"
                              onClick={() =>
                                handleEditClick(rec.id, index, data, canEdit)
                              }
                            >
                              Edit
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      ));
                    })()
                  : "-"}
              </TableCell> */}

              <TableCell className="text-center">
                <div>{Number(data.balance_amt ?? 0).toFixed(2)}</div>
                {Array.isArray(data.used_details) &&
                  data.used_details.length > 0 && (
                    <div
                      className="text-xs text-orange-600 mt-1"
                      title="Money from this booking already given to another booking's payment"
                    >
                      Used: ₹{Number(data.used_amount ?? 0).toFixed(2)}
                      {data.used_details.map((ud, uidx) => (
                        <div key={uidx}>
                          → {ud.used_in_ref_no || ud.used_in_client_id}: ₹
                          {Number(ud.used_amount).toFixed(2)}
                        </div>
                      ))}
                    </div>
                  )}
                {parseFloat(data.over_adjusted_amount) > 0 && (
                  <div
                    className="text-xs text-red-700 font-semibold mt-1"
                    title="More was adjusted away from this booking than it ever genuinely had as excess — needs manual correction (pull back from the receiving booking, or collect this amount here)"
                  >
                    ⚠ Over-adjusted: ₹
                    {Number(data.over_adjusted_amount).toFixed(2)}
                  </div>
                )}
                {parseFloat(data.receivable_amt) > 0 &&
                  parseFloat(data.balance_amt) > 0 &&
                  // Once this booking's overpaid excess has already been
                  // given away to another booking via adjustment, a positive
                  // balance here means a correction is needed (the giver
                  // adjustment took more than its true excess), not that
                  // more payment should be collected normally — offering
                  // "Add" here would just create another confusing entry on
                  // an already-reconciled-elsewhere booking.
                  !(
                    Array.isArray(data.used_details) &&
                    data.used_details.length > 0
                  ) && (
                    <div className="mt-1">
                      <button
                        className="text-blue-500"
                        onClick={() => openReceiveModal(data, index, data)}
                      >
                        Add
                      </button>
                    </div>
                  )}
              </TableCell>

              <TableCell className="text-center">
                {Array.isArray(data.sales_records) &&
                data.sales_records.length > 0 ? (
                  <div className="flex flex-col items-center gap-1">
                    {data.sales_records.map((rec, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          setSelectedRemarks([rec]);
                          setOpenRemarkModal(true);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        Remark
                      </div>
                    ))}
                  </div>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="text-center">
                {(() => {
                  const status = data.display_status;
                  if (!status) {
                    return (
                      <span className="text-sm font-medium text-red-500">
                        Pending
                      </span>
                    );
                  }
                  const colorClass =
                    {
                      green: "text-green-500",
                      red: "text-red-500",
                      yellow: "text-yellow-500",
                    }[status.color] || "text-gray-500";
                  return (
                    <span className={`text-sm font-medium ${colorClass}`}>
                      {status.label}
                      {status.amount !== null && status.amount !== undefined
                        ? `: ${status.amount}`
                        : ""}
                    </span>
                  );
                })()}
              </TableCell>
              <TableCell className="text-center">
                {data.trip_status === 1 ? (
                  data.cancelled_at ? (
                    <div className="flex flex-col items-center space-y-1">
                      <span className="text-red-500 text-sm">
                        {new Date(data.cancelled_at).toLocaleString()}
                      </span>
                      <button
                        onClick={() => handleShowRemark(data.cancel_reason)}
                        className="text-blue-600 hover:underline text-sm focus:outline-none"
                      >
                        Remark
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Active</span>
                  )
                ) : (
                  <span className="text-gray-400 text-sm">-</span>
                )}
              </TableCell>

              <TableCell className="text-center">
                <button
                  onClick={() => handleViewClick(data.client_id)}
                  className="p-2 text-gray-400 hover:text-blue-600 focus:outline-none"
                >
                  <Tooltip
                    id={`view-${data.client_id}`}
                    Icon={FiEye}
                    title="View"
                    bgColor="#3B82F6"
                  />
                </button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>

      {/* { console.log(receiveRowData)} */}
      {showReceiveModal && (
        <ReceivedAmtModal
          isOpen={showReceiveModal}
          data={receiveRowData}
          canEdit={cannotEdit}
          mode={modalMode}
          onClose={() => {
            setShowReceiveModal(false);
            setReceiveRowData(null);
            setModalMode("add");
          }}
          onSuccess={handleModalSuccess}
        />
      )}
      {showSalesModal && (
        <SalesViewModal
          open={showSalesModal}
          onClose={() => setShowSalesModal(false)}
          data={selectedSalesData}
        />
      )}
      {openRemarkModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setOpenRemarkModal(false)}
        >
          <div
            className="relative bg-white p-8 rounded-xl w-[600px] max-h-[85vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-6 text-center">
              Sales Remarks
            </h2>
            {selectedRemarks.map((rec, index) => (
              <div
                key={index}
                className="mb-4 border-b pb-2 text-base text-gray-700"
              >
                {rec.remark || "-"}
              </div>
            ))}
          </div>
        </div>
      )}
      {remarkModalVisible && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={handleCloseModal} // click on backdrop closes modal
        >
          <div
            className="bg-white rounded shadow-lg w-[400px] relative pt-6 pb-8 px-8"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside modal
          >
            {/* Close (X) icon in top-right corner */}

            <h2 className="text-xl font-semibold mb-4">Cancellation Remark</h2>
            <p className="text-gray-700">{remarkText}</p>
          </div>
        </div>
      )}
      {openEditPriceModal && (
        <Modal
          isOpen={openEditPriceModal}
          onClose={() => setOpenEditPriceModal(false)}
        >
          <ModalBody>
            <p className="font-bold mb-2">Edit Selling Price</p>

            <input
              type="number"
              className="border w-full p-2 rounded"
              value={newSellingPrice}
              onChange={(e) => setNewSellingPrice(e.target.value)}
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                className="px-6 py-2 bg-gray-300 rounded"
                onClick={() => setOpenEditPriceModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-6 py-2 bg-green-500 text-white rounded"
                onClick={() => setConfirmUpdateModal(true)}
              >
                Update
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}

      {confirmUpdateModal && (
        <Modal
          isOpen={confirmUpdateModal}
          onClose={() => setConfirmUpdateModal(false)}
        >
          <ModalBody>
            <p className="text-lg font-semibold mb-3">
              Are you sure want to update?
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => setConfirmUpdateModal(false)}
              >
                No
              </button>

              <button
                className="px-4 py-2 bg-red-500 text-white rounded"
                onClick={() => updateSellingPriceAPI(selectedUpdateId)}
              >
                Yes, Update
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default SalesTable;
